import { MutableRefObject, useEffect, useRef, useState } from "react";
import ResizeObserver from "resize-observer-polyfill";

export interface IDimensions {
  height: number;
  width: number;
  radius?: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  boundedHeight: number;
  boundedWidth: number;
  boundedRadius?: number;
}


export const combineChartDimensions = (dimensions: Partial<IDimensions>): IDimensions => {
  const parsedDimensions: IDimensions = {
    height: 0,
    width: 0,
    boundedHeight: 0,
    boundedWidth: 0,
    marginTop: 40,
    marginRight: 30,
    marginBottom: 40,
    marginLeft: 75,
    ...dimensions,
  };

  return {
    ...parsedDimensions,
    boundedHeight: Math.max(parsedDimensions.height - parsedDimensions.marginTop - parsedDimensions.marginBottom, 0),
    boundedWidth: Math.max(parsedDimensions.width - parsedDimensions.marginLeft - parsedDimensions.marginRight, 0),
  };
};

export const useChartDimensions = <T extends HTMLElement>(passedSettings: Partial<IDimensions>): [ MutableRefObject<T | null>, IDimensions] => {
  const ref = useRef<T>(null);
  const dimensions = combineChartDimensions(passedSettings);

  const [width, changeWidth] = useState(0);
  const [height, changeHeight] = useState(0);

  useEffect((): any => {
    if (dimensions.width && dimensions.height) {
      return;
    }

    const element = ref.current;
    const resizeObserver = new ResizeObserver((entries: any[]) => {
      if (!Array.isArray(entries)) {
        return;
      }
      if (!entries.length) {
        return;
      }

      const entry = entries[0];

      if (width !== entry.contentRect.width) {
        changeWidth(entry.contentRect.width);
      }
      if (height !== entry.contentRect.height) {
        changeHeight(entry.contentRect.height);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }

    return () => {
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, [height, width, dimensions]);

  const newWidth = dimensions.width || width;
  const newHeight = dimensions.height || height;
  const boundedHeight = Math.max(newHeight - dimensions.marginTop - dimensions.marginBottom, 0);
  const boundedWidth = Math.max(newWidth - dimensions.marginLeft - dimensions.marginRight, 0);

  if(dimensions.radius) {
    dimensions.radius = Math.min(newWidth, newHeight) / 2.0 - 10;
    dimensions.boundedRadius = Math.min(boundedWidth, boundedHeight) / 2.0 - 10;
  }

  const newSettings = {
    ...dimensions,
    width: newWidth,
    height: newHeight,
    boundedHeight,
    boundedWidth
  };

  return [ref, newSettings];
};


let lastId = 0;
export const useUniqueId = (prefix=""): string => {
  lastId++;

  return [prefix, lastId].join("-");
};

export const getTicks = (domain: number[], count: number ): number[] =>  {
  const ticks = [domain[0]];
  const min = 2;

  if (count > min) {
    const factor = (domain[1] - domain[0]) / (count - 1);
    let pointer = domain[0] + factor;
    for (let i = 2; i < count; i++) {
      ticks.push(pointer);
      pointer += factor;
    }
  }

  ticks.push(domain[1]);

  return ticks;
};

export const getMiddle = (value: number): number => {
  const midDiv = 2;

  return value / midDiv;
};

export const getCoordinatesForAngle = (angle: number, radius: number): number[] => [
  Math.cos(angle - Math.PI / 2) * radius,
  Math.sin(angle - Math.PI / 2) * radius,
];
